import { useState } from 'react';
import type { ReactNode } from 'react';
import type { EChartsCoreOption } from 'echarts/core';
import { Grafico } from './Grafico';
import './graficos.css';

/**
 * CARTÃO DE GRÁFICO — o gráfico que INFORMA: título, o número principal com a variação, a frase
 * com a conclusão (tirada do dado real), o gráfico e a troca de modelo. Cada pessoa escolhe o
 * modelo que prefere e ele fica lembrado neste aparelho. Sem dado: diz que não há, nunca desenha
 * zero de mentira.
 */

function lembrado<M extends string>(
  id: string,
  modelos: readonly (readonly [M, string])[],
  padrao: M,
): M {
  try {
    const v = window.localStorage.getItem(`inovapay:grafico:${id}`);
    return modelos.some(([m]) => m === v) ? (v as M) : padrao;
  } catch {
    return padrao;
  }
}

export function CartaoDeGrafico<M extends string>({
  id,
  titulo,
  numero,
  variacao,
  conclusao,
  modelos,
  padrao,
  desenhar,
  fonte,
  vazio,
  altura = 280,
  largo = false,
  extra,
  topo,
  periodo,
}: {
  /** Chave estável (lembra o modelo escolhido). */
  id: string;
  titulo: string;
  /** O número principal, já formatado. */
  numero?: string | null;
  /** Variação contra o período anterior, em % (positivo = subiu). */
  variacao?: { pct: number | null; bom: 'subir' | 'descer'; base?: string } | null;
  /** A frase que explica o que o gráfico mostra, com o dado real. */
  conclusao: ReactNode;
  modelos: readonly (readonly [M, string])[];
  padrao: M;
  desenhar: (modelo: M) => EChartsCoreOption;
  fonte: string;
  /** Texto quando não há dado no período (o gráfico não é desenhado). */
  vazio?: string | null;
  altura?: number;
  largo?: boolean;
  extra?: ReactNode;
  /** Controles entre a conclusão e o gráfico (ex.: a troca de métrica). */
  topo?: ReactNode;
  /** Seletor de período deste gráfico (ComPeriodo). */
  periodo?: ReactNode;
}) {
  const [modelo, setModelo] = useState<M>(() => lembrado(id, modelos, padrao));
  const trocar = (m: M) => {
    setModelo(m);
    try {
      window.localStorage.setItem(`inovapay:grafico:${id}`, m);
    } catch {
      // sem armazenamento: vale só nesta visita
    }
  };
  const v = variacao?.pct ?? null;
  const tom =
    v === null || Math.abs(v) < 0.05
      ? 'neutro'
      : v > 0 === (variacao?.bom === 'subir')
        ? 'bom'
        : 'ruim';
  return (
    <section className={`gx-cartao${largo ? ' gx-largo' : ''}`}>
      <header className="gx-cabeca">
        <div className="gx-titulos">
          <h3>{titulo}</h3>
          {numero !== undefined && (
            <div className="gx-numero">
              <strong>{numero ?? '—'}</strong>
              {v !== null && (
                <span className={`gx-var gx-var-${tom}`}>
                  {v > 0 ? '▲' : v < 0 ? '▼' : '■'} {Math.abs(v).toFixed(1).replace('.', ',')}%
                  <em> vs. {variacao?.base ?? 'período anterior'}</em>
                </span>
              )}
            </div>
          )}
        </div>
        <div className="gx-controles">
          {periodo}
          {modelos.length > 1 && (
            <label className="gx-modelo">
              <span>Modelo</span>
              <select
                value={modelo}
                onChange={(e) => trocar(e.target.value as M)}
                aria-label={`Modelo do gráfico ${titulo}`}
              >
                {modelos.map(([m, nome]) => (
                  <option key={m} value={m}>
                    {nome}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </header>
      <p className="gx-conclusao">{conclusao}</p>
      {topo}
      {vazio ? (
        <div className="gx-vazio" style={{ height: Math.min(altura, 160) }}>
          {vazio}
        </div>
      ) : (
        <Grafico
          opcao={desenhar(modelo)}
          altura={altura}
          rotulo={`${titulo}: ${typeof conclusao === 'string' ? conclusao : ''}`}
        />
      )}
      {extra}
      <footer className="gx-fonte">{fonte} · passe o mouse (ou toque) para ver os números</footer>
    </section>
  );
}
